import {
    Injectable, inject
} from "@angular/core";
import {
    ModelAction, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    BulkResponse,
    BulkUtil,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse
} from "@shopliftr/common-js/shared";
import {
    ByodStore, ByodStoreSearchFilterOptions
} from "@shopliftr/common-js/smart";
import {
    IReadOnlyInternalApiClientService,
    InternalApiRequestService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * HTTP Request Service for ByodStore entities.
 *
 * This service implements the **Repository Pattern** (API Client Service), providing a clean
 * abstraction for read operations on ByodStore entities. It encapsulates all read functionality
 * including get, getAll, search, and bulkGet, delegating the actual HTTP communication to
 * the InternalApiRequestService.
 *
 * @pattern Repository Pattern / HTTP Request Service
 * @providedIn root
 * @implements {IReadOnlyInternalApiClientService<ByodStore>}
 * @implements {IQueryInternalApiClientService<ByodStore>}
 *
 * @see {@link IReadOnlyInternalApiClientService}
 * @see {@link IQueryInternalApiClientService}
 * @see {@link ByodStore}
 */
@Injectable({ providedIn: "root" })
export class ByodStoreApiClientService implements IReadOnlyInternalApiClientService<ByodStore>,
IQueryInternalApiClientService<ByodStore> {
    /**
     * Base API path for all ByodStore-related endpoints.
     * Used as the root path segment for constructing API URLs.
     */
    readonly basePath = "byod-store";

    /**
     * Injected service responsible for making HTTP requests to the Internal API.
     * Handles the low-level communication details including request formatting,
     * error handling, and response parsing.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves a single ByodStore entity by its unique identifier.
     *
     * @param id - The unique identifier of the ByodStore to retrieve
     * @returns Observable that emits the requested ByodStore entity
     *
     * @example
     * ```typescript
     * this.byodStoreService.get$('store-123').subscribe(store => {
     *   console.log(store.name);
     * });
     * ```
     */
    get$(id: string): Observable<ByodStore> {
        const request = RequestUtil.createGetRequest<ByodStore>(id, ByodStore.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$(request, this.basePath).pipe(
            map((response: GetResponse<ByodStore>) => response.payload)
        );
    }

    /**
     * Performs a paginated search for ByodStore entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     *
     * @example
     * ```typescript
     * this.byodStoreService.search$({ chainId: 'chain-123' }, 0, 20).subscribe(response => {
     *   console.log(`Page ${response.page} of ${response.totalPages}`);
     * });
     * ```
     */
    search$(
        filterOptions: ByodStoreSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodStore>> {
        const request: SearchRequest<ByodStoreSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodStore.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$<ByodStore, ByodStoreSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Retrieves multiple ByodStore entities in a single bulk request.
     * More efficient than making individual get() calls when fetching multiple entities.
     *
     * @param ids - Array of unique identifiers for the ByodStores to retrieve
     * @returns Observable that emits a BulkResponse containing all requested entities
     *
     * @example
     * ```typescript
     * this.byodStoreService.bulkGet$(['id-1', 'id-2']).subscribe(response => {
     *   console.log('Bulk response:', response);
     * });
     * ```
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ByodStore>(id, ByodStore.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves all ByodStore entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration. Defaults to ascending sort by name
     * @returns Observable that emits an array of all matching ByodStore entities
     *
     * @example
     * ```typescript
     * this.byodStoreService.getAll$({ chainId: 'chain-123' }).subscribe(stores => {
     *   console.log(`Found ${stores.length} stores`);
     * });
     * ```
     */
    getAll$(
        filterOptions: ByodStoreSearchFilterOptions,
        sort?: Array<ISearchSort>
    ): Observable<Array<ByodStore>> {

        const request: SearchRequest<ByodStoreSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodStore.modelName,
            undefined,
            undefined,
            filterOptions,
            sort ?? [{
                field: "name",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<ByodStore, ByodStoreSearchFilterOptions>(request, this.basePath);
    }

    /**
     * @deprecated Use search$ instead
     */
    searchCampaign$(
        filterOptions: ByodStoreSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodStore>> {
        return this.search$(filterOptions, page, pageSize, sort);
    }
}
